import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { TreeDeciduous, Pencil, BookOpen, X, Flame, Award, Feather } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TreehousePage = () => {
  const { user, token, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [words, setWords] = useState([]);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [loading, setLoading] = useState(true);

  const avatars = ['🦊', '🦉', '🐻', '🐼', '🐰', '🐧', '🦁', '🐨', '🐯', '🦋', '🐢', '🦄'];

  const themes = [
    { id: 'forest', name: 'Forest', colors: ['#10B981', '#059669'] },
    { id: 'ocean', name: 'Ocean', colors: ['#0EA5E9', '#0284C7'] },
    { id: 'sunset', name: 'Sunset', colors: ['#F97316', '#EA580C'] },
    { id: 'berry', name: 'Berry', colors: ['#EC4899', '#DB2777'] },
    { id: 'night', name: 'Night', colors: ['#6366F1', '#4F46E5'] }
  ];

  useEffect(() => {
    fetchWords();
  }, []);

  const fetchWords = async () => {
    try {
      const res = await fetch(`${API}/words`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWords(data);
      }
    } catch (err) {
      console.error('Failed to fetch words:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateAvatar = async (avatar) => {
    try {
      const res = await fetch(`${API}/user/avatar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ avatar })
      });
      if (res.ok) {
        updateUser({ avatar });
        setShowAvatarPicker(false);
      }
    } catch (err) {
      console.error('Failed to update avatar:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-4xl animate-bounce">🏡</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="treehouse-page">
      {/* Page Hero */}
      <div 
        className="rounded-3xl p-6 text-center"
        style={{ 
          background: `linear-gradient(135deg, var(--th-md), var(--th-dk))`,
          color: 'white'
        }}
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <TreeDeciduous className="w-6 h-6" />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fredoka' }}>
            My Treehouse
          </h1>
        </div>
        <p className="opacity-90">Make it yours — pick your colours and animal! 🌿</p>
      </div>

      {/* Profile Card */}
      <div 
        className="rounded-3xl p-6 border-2"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-4">
          <div className="relative">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl"
              style={{ background: 'var(--background)' }}
            >
              {user?.avatar || '🦊'}
            </div>
            <button
              onClick={() => setShowAvatarPicker(true)}
              data-testid="edit-avatar-btn"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center text-white"
              style={{ background: 'var(--primary)' }}
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex-1">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: 'Fredoka' }}>
              {user?.name || 'Reader'}
            </h2>
            <p style={{ color: 'var(--text-light)' }}>
              {user?.grade || 'Grade 2'} · Age {user?.age || 7}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mt-6">
          <div className="text-center p-3 rounded-xl" style={{ background: 'var(--background)' }}>
            <div className="text-2xl mb-1">📚</div>
            <div className="font-bold" style={{ color: 'var(--text)' }}>{user?.books_read || 0}</div>
            <div className="text-xs" style={{ color: 'var(--text-light)' }}>Books</div>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: 'var(--background)' }}>
            <div className="text-2xl mb-1">🍃</div>
            <div className="font-bold" style={{ color: 'var(--text)' }}>{user?.leaf_points || 0}</div>
            <div className="text-xs" style={{ color: 'var(--text-light)' }}>Points</div>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: 'var(--background)' }}>
            <div className="text-2xl mb-1">🔥</div>
            <div className="font-bold" style={{ color: 'var(--text)' }}>{user?.streak_days || 0}</div>
            <div className="text-xs" style={{ color: 'var(--text-light)' }}>Streak</div>
          </div>
          <div className="text-center p-3 rounded-xl" style={{ background: 'var(--background)' }}>
            <div className="text-2xl mb-1">📝</div>
            <div className="font-bold" style={{ color: 'var(--text)' }}>{user?.words_saved || 0}</div>
            <div className="text-xs" style={{ color: 'var(--text-light)' }}>Words</div>
          </div>
        </div>
      </div>

      {/* Theme Picker */}
      <div 
        className="rounded-3xl p-6 border-2"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <h3 className="font-bold mb-4" style={{ color: 'var(--text)', fontFamily: 'Fredoka' }}>
          Choose your theme
        </h3>
        
        <div className="grid grid-cols-5 gap-3">
          {themes.map(t => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              data-testid={`theme-picker-${t.id}`}
              className={`rounded-2xl p-3 transition-all ${
                theme === t.id ? 'ring-4 ring-offset-2 scale-105' : 'hover:scale-102'
              }`}
              style={{ 
                background: 'var(--background)',
                ringColor: t.colors[0]
              }}
            >
              <div 
                className={`h-12 rounded-xl mb-2 relative overflow-hidden ${theme === t.id ? 'ring-2 ring-white' : ''}`}
                style={{ background: `linear-gradient(135deg, ${t.colors[0]}, ${t.colors[1]})` }}
              >
                {theme === t.id && (
                  <div className="absolute inset-0 flex items-center justify-center text-white font-bold">
                    ✓
                  </div>
                )}
              </div>
              <div className="text-sm font-medium text-center" style={{ color: 'var(--text)' }}>
                {t.name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Word Collection */}
      <div 
        className="rounded-3xl p-6 border-2"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--primary)', color: 'white' }}
            >
              <BookOpen className="w-5 h-5" />
            </div>
            <h3 className="font-bold" style={{ color: 'var(--text)', fontFamily: 'Fredoka' }}>
              My Word Collection
            </h3>
          </div>
          <span 
            className="px-3 py-1 rounded-full text-sm font-medium"
            style={{ background: 'var(--background)', color: 'var(--text-light)' }}
          >
            {words.length} words
          </span>
        </div>

        {words.length === 0 ? (
          <div className="text-center py-8" style={{ color: 'var(--text-light)' }}>
            <div className="text-4xl mb-2">📖</div>
            <p>No words yet — tap any word while reading to save it here!</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {words.map(word => (
              <div
                key={word.id}
                className="flex items-center gap-3 p-3 rounded-xl"
                style={{ background: 'var(--background)' }}
              >
                <span className="text-xl">{word.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold capitalize" style={{ color: 'var(--text)' }}>
                    {word.word}
                  </div>
                  <div className="text-sm truncate" style={{ color: 'var(--text-light)' }}>
                    {word.definition}
                  </div>
                </div>
                <span className="text-yellow-500">⭐</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="rounded-3xl p-6 w-full max-w-md animate-bounce-in"
            style={{ background: 'var(--card)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: 'Fredoka' }}>
                Choose Your Avatar
              </h3>
              <button
                onClick={() => setShowAvatarPicker(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: 'var(--background)' }}
              >
                <X className="w-5 h-5" style={{ color: 'var(--text-light)' }} />
              </button>
            </div>
            
            <div className="grid grid-cols-4 gap-3">
              {avatars.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => updateAvatar(emoji)}
                  data-testid={`avatar-${emoji}`}
                  className={`p-4 rounded-2xl text-3xl transition-all btn-bounce ${
                    user?.avatar === emoji ? 'ring-4' : ''
                  }`}
                  style={{ 
                    background: 'var(--background)',
                    ringColor: 'var(--primary)'
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreehousePage;
